import { Info } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import type { LeaveBalance } from '../../lib/leaveApi';
import { Skeleton } from '../ui/Skeleton';

/**
 * "Zile de odihnă" — the four numbers that matter on the caller's own tab.
 *
 * Only ANNUAL leave draws down the balance (the server decides that, see
 * `LeaveBalance.byType`), which is why the hint line is always shown: without
 * it people read "Rămase" as "days of absence left of any kind" and file a
 * sick leave expecting the counter to move.
 */
export function LeaveBalanceCard({
  balance,
  isLoading,
}: {
  balance: LeaveBalance | undefined;
  isLoading?: boolean;
}) {
  const { t } = useI18n();

  if (isLoading || !balance) {
    return (
      <div className="rounded-xl border border-border bg-card p-4">
        <Skeleton className="h-4 w-40" />
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const cells: { label: string; value: number; strong?: boolean }[] = [
    { label: t('leave.balance.entitled'), value: balance.entitledDays },
    { label: t('leave.balance.used'), value: balance.usedDays },
    { label: t('leave.balance.pending'), value: balance.pendingDays },
    { label: t('leave.balance.remaining'), value: balance.remainingDays, strong: true },
  ];

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h2 className="font-heading text-sm font-semibold text-foreground">
        {t('leave.balance.title', { year: balance.year })}
      </h2>

      {/* Two columns on a phone, four from `sm` up — the card sits above the
          request list, so it must not force the page to scroll sideways. */}
      <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {cells.map((cell) => (
          <div key={cell.label}>
            <div
              className={
                cell.strong
                  ? 'text-2xl font-semibold text-foreground'
                  : 'text-2xl font-medium text-muted-foreground'
              }
            >
              {cell.value}
            </div>
            <div className="mt-0.5 text-xs uppercase tracking-wide text-gray-500">{cell.label}</div>
          </div>
        ))}
      </div>

      <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
        <Info size={13} className="mt-0.5 shrink-0" />
        <span>
          {t('leave.balance.hint')}
          {/* Nobody set a personal entitlement, so these are the house days —
              say so, and say where they can be changed. */}
          {balance.isDefaultEntitlement && <> {t('leave.balance.default')}</>}
        </span>
      </p>
    </div>
  );
}
